import type { DragEvent } from 'react';
import { NODE_STYLES, type NodeKind } from './node-types';

const KINDS: NodeKind[] = [
  'message',
  'input',
  'condition',
  'condition_vars',
  'menu',
  'retry',
  'schedule_table',
  'transfer_human',
  'wait',
  'webhook',
  'end',
];

interface VariableHint {
  token: string;
  description: string;
}

const DEFAULT_VARIABLES: VariableHint[] = [
  { token: '{{firstname}}', description: 'primeiro nome' },
  { token: '{{name}}', description: 'nome completo' },
  { token: '{{phone}}', description: 'WhatsApp' },
  { token: '{{email}}', description: 'email' },
  { token: '{{state_uf}}', description: 'UF' },
  { token: '{{message.body}}', description: 'última entrada' },
  { token: '{{state.X}}', description: 'variável capturada' },
];

export function Palette({ extraVariables = [] }: { extraVariables?: VariableHint[] }) {
  const onDragStart = (e: DragEvent<HTMLDivElement>, kind: NodeKind) => {
    e.dataTransfer.setData('application/reactflow-node-kind', kind);
    e.dataTransfer.effectAllowed = 'move';
  };

  const variables = [...DEFAULT_VARIABLES, ...extraVariables];

  return (
    <aside className="w-[240px] min-w-[240px] overflow-y-auto border-n-200 border-r bg-n-0 p-[14px]">
      <h4 className="mb-2 mt-0 font-bold text-[11px] text-n-500 uppercase tracking-[.06em]">
        Tipos de nó
      </h4>
      {KINDS.map((kind) => {
        const s = NODE_STYLES[kind];
        const Icon = s.icon;
        return (
          <div
            key={kind}
            draggable
            onDragStart={(e) => onDragStart(e, kind)}
            className="mb-[6px] flex cursor-grab items-center gap-[10px] rounded-md border border-n-200 bg-n-0 p-[10px_12px] transition-all hover:border-brand-400 hover:bg-brand-50 hover:shadow-sm"
            title={s.description}
          >
            <span
              className={`flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-sm ${s.bgClass}`}
            >
              <Icon size={16} strokeWidth={1.75} className="text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[13px] text-n-900">{s.label}</div>
              <div className="mt-[1px] truncate text-[11px] text-n-500">{s.description}</div>
            </div>
          </div>
        );
      })}

      <h4 className="mb-2 mt-4 font-bold text-[11px] text-n-500 uppercase tracking-[.06em]">
        Variáveis
      </h4>
      <div className="space-y-[6px] text-[12px] text-n-700">
        {variables.map((v) => (
          <div key={v.token} className="flex items-center gap-2">
            <code className="rounded bg-n-100 px-1.5 py-[1px] font-mono text-[11px] text-brand-700">
              {v.token}
            </code>
            <span className="truncate text-[11px] text-n-500">{v.description}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-n-500">
        Use <code className="font-mono">{'{{state.<nome>}}'}</code> em mensagens pra interpolar
        valores capturados pelo nó <b>Entrada</b>.
      </p>
    </aside>
  );
}
